
import React, { useEffect, useState } from "react";
import client from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { PREMIUM } from "@/constants/testIds";
import { Crown, Check } from "lucide-react";
import { toast } from "sonner";

const FEATURES = {
    premium_monthly: [
        "Unlimited messaging",
        "View contact details",
        "Priority profile visibility",
        "Advanced search filters",
        "Read receipts",
    ],
    premium_yearly: [
        "Everything in Monthly",
        "Save 33% vs monthly",
        "Profile boost ×2 per month",
        "Priority support",
        "Verified badge fast-track",
    ],
    elite_lifetime: [
        "Everything in Yearly",
        "Lifetime access",
        "Personal matchmaker concierge",
        "Featured on homepage rotation",
        "Profile boost unlimited",
    ],
};

export default function Premium() {
    const { user } = useAuth();
    const [pkgs, setPkgs] = useState({});
    const [busy, setBusy] = useState(null);

    useEffect(() => {
        client.get("/payments/packages").then(({ data }) => setPkgs(data)).catch(() => {});
    }, []);

    const checkout = async (pkgId) => {
        setBusy(pkgId);
        try {
            const { data } = await client.post("/payments/checkout", {
                package_id: pkgId,
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (e) {
            toast.error(e.response?.data?.detail || "Checkout failed");
            setBusy(null);
        }
    };

    const isPremium = user?.premium_until && new Date(user.premium_until) > new Date();

    return (
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-16">
            <div className="text-center max-w-3xl mx-auto">
                <div className="inline-flex items-center gap-2 bg-gold/10 border border-gold/30 rounded-lg px-4 py-1.5 text-[11px] tracking-[0.2em] uppercase text-gold-700 font-semibold">
                    <Crown className="h-3.5 w-3.5" /> Premium Plans
                </div>
                <h1 className="mt-6 font-serif text-5xl md:text-6xl text-navy tracking-tighter">Find your beloved <span className="italic text-gold-600">faster</span></h1>
                <p className="mt-4 text-navy-500 font-sans">Unlock unlimited messaging, contact access, and priority visibility.</p>
                {isPremium && (
                    <div className="mt-6 inline-block bg-navy text-gold rounded-lg px-5 py-2 text-xs uppercase tracking-[0.18em] font-semibold">
                        Premium active until {new Date(user.premium_until).toLocaleDateString()}
                    </div>
                )}
            </div>

            <div className="mt-16 grid md:grid-cols-3 gap-6">
                {Object.entries(pkgs).map(([id, pkg], i) => {
                    const featured = i === 1;
                    return (
                        <div key={id} className={`relative rounded-3xl p-8 transition-all ${featured ? "bg-navy text-white shadow-2xl shadow-navy/30 scale-105" : "bg-white border border-navy-100 hover:shadow-lg"}`}>
                            {featured && (
                                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gold text-navy rounded-lg px-4 py-1 text-[10px] uppercase tracking-[0.2em] font-semibold">
                                    Most Popular
                                </div>
                            )}
                            <div className={`text-[11px] tracking-[0.25em] uppercase font-semibold ${featured ? "text-gold-300" : "text-gold-700"}`}>
                                {id === "elite_lifetime" ? "Elite" : id === "premium_yearly" ? "Best Value" : "Starter"}
                            </div>
                            <h3 className={`mt-2 font-serif text-3xl ${featured ? "text-white" : "text-navy"}`}>{pkg.name}</h3>
                            <div className="mt-4 flex items-baseline gap-1">
                                <span className={`font-serif text-5xl ${featured ? "text-white" : "text-navy"}`}>${pkg.amount}</span>
                                <span className={`text-sm font-sans ${featured ? "text-navy-200" : "text-navy-400"}`}>
                                    /{pkg.duration_days === 30 ? "mo" : pkg.duration_days === 365 ? "yr" : "lifetime"}
                                </span>
                            </div>
                            <ul className={`mt-6 space-y-3 text-sm font-sans ${featured ? "text-navy-100" : "text-navy-600"}`}>
                                {(FEATURES[id] || []).map((f) => (
                                    <li key={f} className="flex gap-2"><Check className={`h-4 w-4 shrink-0 ${featured ? "text-gold" : "text-gold-600"}`} /> {f}</li>
                                ))}
                            </ul>
                            <button
                                onClick={() => checkout(id)}
                                disabled={busy === id}
                                data-testid={`${PREMIUM.select}-${id}`}
                                className={`mt-8 w-full font-sans text-xs uppercase tracking-[0.18em] rounded-lg px-6 py-4 active:scale-95 transition-all disabled:opacity-50 ${
                                    featured ? "bg-gold text-navy hover:bg-white shadow-lg shadow-gold/30" : "bg-navy text-white hover:bg-navy-700"
                                }`}
                            >
                                {busy === id ? "Loading…" : "Choose " + pkg.name}
                            </button>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
